import React, { useCallback } from 'react';

import { Radio } from '@rbilabs/universal-components';
import { StyleSheet } from 'react-native';

import ActionButton from 'components/action-button';
import { Box, Typography } from 'components/ui';
import { SORTED_PROD_LOCALES } from 'state/intl/constants';
import { t } from 'state/intl/translations';
import { LANGUAGE_TO_LABEL } from 'state/intl/types';
import { spacing } from 'styles/constants';
import { regionEnum } from 'utils/environment';

interface ILanguageModalContentsProps {
  disclaimer?: string;
  applyButtonText: string;
  setLanguage: () => void;
  handleSelection: (selectedLanguage: string, mappedRegion: string, isOneStep: boolean) => void;
  regionKey: string;
  langKey: string;
  /**
   * Pass the id of the heading that describes the radio group.
   */
  ariaRadioGroupLabeledBy: string;
}

const LanguageSelectorModalContents: React.FC<React.PropsWithChildren<
  ILanguageModalContentsProps
>> = ({
  regionKey,
  langKey,
  disclaimer,
  applyButtonText,
  setLanguage,
  handleSelection,
  ariaRadioGroupLabeledBy = '',
}) => {
  const regionLanguageSelections = SORTED_PROD_LOCALES.filter(l => l.region === regionEnum).map(
    ({ region: mappedRegion, language: mappedLanguage }) => (
      <Box
        key={`dialog-button-${mappedLanguage}-${mappedRegion}`}
        style={styles.languageSelectionCell}
      >
        <Radio
          value={`${mappedLanguage}-${mappedRegion}`}
          testID={`dialog-button-${mappedLanguage.toLowerCase()}-${mappedRegion.toLowerCase()}`}
          accessibilityLabel={`${t('select')} ${
            LANGUAGE_TO_LABEL[mappedLanguage]
          } / ${mappedRegion}`}
        >
          <Box style={styles.languageLabelWrapper}>
            <Typography
              variant="headerThree"
              style={styles.languageLabel}
              data-lang={mappedLanguage.toLowerCase()}
            >
              {LANGUAGE_TO_LABEL[mappedLanguage]}
            </Typography>
          </Box>
        </Radio>
      </Box>
    )
  );

  const handleRadioChange = useCallback(
    (value: string) => {
      const [mappedLanguage, mappedRegion] = value.split('-');
      handleSelection(mappedLanguage, mappedRegion, false);
    },
    [handleSelection]
  );

  return (
    <>
      <Radio.Group
        name={ariaRadioGroupLabeledBy}
        value={`${langKey}-${regionKey}`}
        onChange={handleRadioChange}
        accessibilityRole="radiogroup"
        aria-labelledby={ariaRadioGroupLabeledBy}
        style={styles.languageSelectionContainer}
      >
        {regionLanguageSelections}
      </Radio.Group>
      {disclaimer && (
        <Box style={styles.modalAdditionalContentWrapper}>
          <Typography fontSize="sm" style={styles.disclaimer}>
            {disclaimer}
          </Typography>
        </Box>
      )}
      <Box style={styles.actionsWrapper}>
        <ActionButton onPress={setLanguage} testID="action-button-apply-language-selection">
          {applyButtonText}
        </ActionButton>
      </Box>
    </>
  );
};

export default LanguageSelectorModalContents;

const styles = StyleSheet.create({
  actionsWrapper: {
    alignContent: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.$8,
  },
  disclaimer: {
    marginBottom: spacing.$6,
    textAlign: 'center',
  },
  languageLabel: {
    marginBottom: spacing.$0,
  },
  languageLabelWrapper: {
    marginLeft: spacing.$2,
  },
  languageSelectionCell: {
    paddingHorizontal: spacing.$6,
    paddingVertical: spacing.$3,
  },
  languageSelectionContainer: {
    marginBottom: spacing.$5,
    marginHorizontal: 'auto',
    marginTop: spacing.$0,
  },
  modalAdditionalContentWrapper: {
    paddingHorizontal: spacing.$5,
  },
});
